import React from "react";
import { Reveal, SectionLabel } from "../components/primitives";

const SECTORS = [
  { id: "01", name: "EV & Mobility", size: "$28B", subs: ["Integrated Motor-Controller Systems", "India-Tuned Battery Pack Systems", "Retrofit xEV Platforms"] },
  { id: "02", name: "Energy Storage", size: "$21B", subs: ["LFP & advanced chemistries", "Long-duration storage", "Flow battery membranes"] },
  { id: "03", name: "Industrial Decarbonization", size: "$25B", subs: ["Waste Heat Recovery (highest conviction)", "Sector-specific carbon monitoring", "Novel carbon capture"] },
  { id: "04", name: "Green Hydrogen", size: "$7.5B", subs: ["AEM electrolyser stacks", "Electrolyser power electronics", "Domestic membrane manufacturing"] },
  { id: "05", name: "Built Environment & Infrastructure", size: "$14B", subs: ["Whole-system HVAC optimisation", "Data-centre thermal management", "Geopolymer cement & alt materials"] },
  { id: "06", name: "Power Electronics", size: "$14B", subs: ["SiC bidirectional EV chargers · V2G", "GaN power electronics for BoP", "Integrated motor drive controllers"] },
  { id: "07", name: "Carbon Monitoring", size: "$5B+", subs: ["Sector-specific MRV", "CBAM-grade sensor stacks", "Industrial reporting platforms"] },
  { id: "08", name: "Circular Economy", size: "—", subs: ["Hydrometallurgical battery recycling", "Bio-based materials", "Second-life EV battery systems"] },
  { id: "09", name: "Alternative Fuels", size: "—", subs: ["CBG plants with co-products", "E-fuel platforms", "Distributed bio-refineries"] },
  { id: "10", name: "Advanced Manufacturing & Supply Chain Tech", size: "—", subs: ["Precision manufacturing", "Robotics for industrial use", "Factory automation", "Localized supply chain platforms"] },
  { id: "11", name: "Data Centers & Energy-Heavy Infra", size: "—", subs: ["Data center cooling", "Energy optimization", "AI infra energy systems"] },
  { id: "12", name: "Next-Gen Materials", size: "—", tag: "Underrated but huge", subs: ["Green cement / geopolymer", "Lightweight materials", "Thermal materials", "Industrial coatings"] },
  { id: "13", name: "Digital & Intelligence Layer", size: "—", subs: ["Enabling software & AI platforms", "Optimisation for physical systems", "Infrastructure orchestration tools"] },
];

const TESTS = [
  { num: "01", title: "The Problem Test", body: "Large, structural, growing problem. Not niche. Not pilot-stage. Measurable in billions." },
  { num: "02", title: "The Technology Test", body: "Defensible tech or operational moat. Proprietary IP, process or domain expertise hard to copy." },
  { num: "03", title: "The ESG Alpha Test", body: "Green positioning generates real business advantage — lower cost, better access, premium pricing." },
];

export default function Philosophy() {
  return (
    <div data-testid="philosophy-page">
      <section className="pt-20 pb-12 lg:pt-32 lg:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionLabel>Philosophy & Opportunity</SectionLabel>
          <h1 className="font-display font-bold tracking-[-0.035em] leading-[0.94] text-5xl sm:text-7xl lg:text-[104px] text-[#111A15] max-w-5xl">
            Impact + returns.<br /><span className="text-[#C44900]">Not a trade-off.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-[17px] text-[#4A5E51] leading-relaxed">
            Thesis-driven, bottom-up sector analysis with TRL discipline. We invest seed to Series A — when the product is ready and the team is hunting for product-fit. Every conviction passes three tests.
          </p>
        </div>
      </section>

      {/* THREE TESTS */}
      <section className="py-16 lg:py-24 border-y border-[#E5E5E0] bg-[#E8ECE9]/40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
          {TESTS.map((t, i) => (
            <Reveal key={t.num} delay={i * 90}>
              <div data-testid={`thesis-test-${t.num}`} className="bg-white p-8 lg:p-10 h-full">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C44900]">— {t.num}</span>
                <h3 className="font-display font-semibold tracking-tight text-2xl lg:text-3xl mt-4 text-[#111A15]">{t.title}</h3>
                <p className="mt-4 text-[14px] text-[#4A5E51] leading-relaxed">{t.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OPPORTUNITY STATS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionLabel>The Opportunity</SectionLabel>
              <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#111A15] leading-[1.02]">
                The problem is the opportunity.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-[15px] text-[#4A5E51] leading-relaxed">
                60 GT global emissions in 2023. A $3T annual clean-energy gap. India industry & power alone account for 74% of national emissions. CBAM enforcement goes live in 2026. This isn't a niche — it's one of the largest capital allocation moments in history.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12">
            {[
              ["$200B+", "India energy transition by 2030"],
              ["30–40%", "Mfg. cost vs China"],
              ["60–70%", "Mfg. cost vs USA"],
              ["$1.17T", "Annual transport gap"],
            ].map(([v, l], i) => (
              <Reveal key={l} delay={i * 70}>
                <div className="border-t border-[#111A15] pt-5">
                  <div className="font-display font-bold tracking-tighter text-3xl sm:text-4xl text-[#111A15]">{v}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] mt-3">{l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 9 ESG SECTORS GRID */}
      <section className="py-24 lg:py-32 bg-[#111A15] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9DB0A3] mb-5">— Thirteen ESG sectors</p>
          <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-14 max-w-3xl">
            Where we deploy conviction.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {SECTORS.map((s, i) => (
              <Reveal key={s.id} delay={i * 40}>
                <div data-testid={`philosophy-sector-${s.id}`} className="bg-[#111A15] p-7 lg:p-8 h-full hover:bg-[#1A2F24] transition-colors">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C44900]">— {s.id}</span>
                    <span className="font-mono text-[12px] text-[#9DB0A3]">{s.size}</span>
                  </div>
                  <h3 className="font-display font-semibold tracking-tight text-2xl mb-2 leading-snug text-white">{s.name}</h3>
                  {s.tag && <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#C44900] mb-4">— {s.tag}</p>}
                  <ul className={`space-y-2 text-[13px] text-[#9DB0A3] leading-relaxed ${s.tag ? "" : "mt-3"}`}>
                    {s.subs.map((sub) => (
                      <li key={sub} className="flex gap-2"><span className="text-[#C44900]">·</span> {sub}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT TARGETS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <SectionLabel>Impact + Returns</SectionLabel>
              <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#111A15] leading-[1.05]">
                Institutional returns. Measurable impact. Same portfolio.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-[15px] text-[#4A5E51] leading-relaxed">
                Every company in the portfolio is underwritten on three dimensions — financial return, ESG alpha, and system-level impact. We report on all three. Climate outcomes aren't a side-effect of our capital; they're the thesis.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12">
            {[
              ["5–10x", "Target MOIC", "Fund-level"],
              ["22–28%", "Target Net IRR", "Net of fees & carry"],
              ["50M+", "tCO₂e avoided", "Cumulative portfolio ambition by 2035"],
              ["₹2,500 Cr+", "MSME financing enabled", "Via Folks Finance green lending"],
            ].map(([v, l, h], i) => (
              <Reveal key={l} delay={i * 70}>
                <div className="border-t border-[#E5E5E0] pt-5">
                  <div className="font-display font-bold tracking-tighter text-3xl sm:text-4xl text-[#111A15]">{v}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] mt-3">{l}</div>
                  {h && <div className="text-[12px] text-[#4A5E51] mt-1 leading-snug">{h}</div>}
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {[
              { kicker: "— Financial", title: "Power-law returns", body: "Conviction-led positions in seed → Series A. Concentrated portfolio. Proactive exit planning." },
              { kicker: "— ESG Alpha", title: "Competitive advantage", body: "Green positioning that creates lower cost, better market access and premium pricing — not just compliance." },
              { kicker: "— System Impact", title: "Infrastructure at scale", body: "Each investment is evaluated on avoided emissions, enabled MSMEs, and contribution to India's transition." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 90}>
                <div className="bg-white p-8 lg:p-10 h-full">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C44900]">{c.kicker}</span>
                  <h3 className="font-display font-semibold tracking-tight text-2xl text-[#111A15] mt-3 mb-4">{c.title}</h3>
                  <p className="text-[14px] text-[#4A5E51] leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
